/**
 * Not on the Website — the cars that are for sale and should be on the
 * dealer's website but are not, what they are worth, how long they have been
 * dark, and the line over time (revenue-assurance plan, Phase 3).
 *
 * Facts only, no attribution. Ian, 2026-10-06: "just present days dark and the
 * [dollar total] and the goal is for it to get smaller. and we dont mark who
 * fixed it." The dollar figure is the HomeNet Selling Price of the dark cars
 * added up — Dolan's own number, not the website's +$599 one — and the page
 * says so beneath it; cars with no price are counted as cars and left out of
 * the total, with that count shown. The sold-cars tile is the other direction
 * (a listing for a car the store no longer has) and stays a count, never
 * dollars. Nothing here is estimated, and the page never calls the number a
 * valuation or "unexposed revenue" — both readings were rejected.
 *
 * Lives under the audit like Tool Performance and is reached from the "Not on
 * the website" tile at the end of the audit page's band. Approved mockup:
 * .claude/plans/revenue-assurance-dashboard-mockup.html.
 *
 * Full width like the audit page (Ian, 2026-10-08: a capped, left-leaning page
 * read as off-centre) and laid out for a phone with `useIsMobile()`: the
 * header stacks, the tiles go to one column, the charts stack and draw at the
 * narrow width, and the dark-cars table becomes a card list.
 */
import React, { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import ErrorBoundary from "../../components/ErrorBoundary"
import AuditStoreTabs, { ALL_ROOFTOPS, readRememberedStore, rememberStore } from "../../components/audit/AuditStoreTabs"
import RunStackedChart, { StackedPoint } from "../../components/audit/notOnWebsite/RunStackedChart"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { AuditRooftopOption, listAuditRooftops } from "../../api/audit"
import {
  NotOnWebsiteResponse,
  NotOnWebsiteVehicle,
  exportNotOnWebsiteWorkbook,
  formatSellingPrice,
  getNotOnWebsite,
} from "../../api/notOnWebsite"
import { THEME } from "../../config/theme"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"

const CRITICAL = THEME.problemDisplay.severityColors.critical
const MUTED = THEME.colors.neutral.light.textSecondary
const INK = THEME.colors.neutral.light.textPrimary
const BORDER = THEME.colors.neutral.light.border
/** The one-third column's chart is drawn at half the wide chart's width so
 * its labels stay the same size on screen (the mockup's 400 to 820). On a
 * phone both charts fill the width, so both draw at this size. */
const NARROW_CHART_WIDTH = 400
/** A sold car still listed is the page's second serious problem, in its own
 * colour so it reads as different from the red cars, not milder (Ian, 2026-10-09). */
const SOLD = THEME.problemDisplay.soldListingAccent
const SOLD_SOFT = THEME.problemDisplay.soldListingSoft

/** The dealer's website by name — "dolanautogroup.com" — or the plain phrase
 * when no site is configured. Ian, 2026-10-09: "it shouldnt be not on the
 * website it should be not on Dolanautogroup.com". */
function siteName(domain: string | null | undefined): string {
  return domain || "the website"
}

export default function NotOnWebsitePage() {
  return (
    <Layout>
      <NotOnWebsiteContent />
    </Layout>
  )
}

/** Exported for tests, which render it without the app shell. */
export function NotOnWebsiteContent() {
  const { dealerGroupId, websiteDomain } = useDealerGroup()
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()
  // undefined = still loading; null = the group has no audit run yet.
  const [page, setPage] = useState<NotOnWebsiteResponse | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  // The store view, exactly as the audit page keeps it (Ian, 2026-10-09: a
  // Dodge manager lands on Dodge's numbers): a convenience default, not a
  // boundary. Seeded URL → this visit's last choice → the user's home store
  // → all stores, remembered the same way the audit page remembers it.
  const [rooftopOptions, setRooftopOptions] = useState<AuditRooftopOption[]>([])
  const [rooftopSlug, setRooftopSlug] = useState<string>(
    () =>
      searchParams.get("rooftop") ??
      readRememberedStore(user?.id) ??
      user?.rooftop?.slug ??
      ALL_ROOFTOPS
  )
  useEffect(() => {
    if (dealerGroupId === null) {
      setRooftopOptions([])
      return undefined
    }
    let cancelled = false
    listAuditRooftops(dealerGroupId)
      .then((response) => {
        if (cancelled) return
        setRooftopOptions(response.rooftops)
        // A stale slug (another group's URL, a renamed store) falls back to
        // the home store or All stores instead of erroring every fetch.
        setRooftopSlug((current) => {
          if (current === ALL_ROOFTOPS) return current
          if (response.rooftops.some((r) => r.slug === current)) return current
          const home = user?.rooftop?.slug
          return home && response.rooftops.some((r) => r.slug === home) ? home : ALL_ROOFTOPS
        })
      })
      .catch(() => {
        if (!cancelled) setRooftopOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, user])
  useEffect(() => {
    rememberStore(user?.id, rooftopSlug)
    setSearchParams(
      (params) => {
        if (rooftopSlug === ALL_ROOFTOPS) params.delete("rooftop")
        else params.set("rooftop", rooftopSlug)
        return params
      },
      { replace: true }
    )
  }, [rooftopSlug, setSearchParams, user?.id])
  // The site's name comes with the login (or the group switcher), so the
  // heading is right on the first paint. Once the page's own answer is in it
  // is the fresher one (a site configured after this login still names
  // itself), and undefined means not known yet: the heading says nothing
  // until it is.
  const siteDomain: string | null | undefined = page ? page.website_domain : websiteDomain
  // Applied only once the loaded options confirm the slug; until then the
  // page reads as All stores rather than erroring on a slug the backend
  // would refuse.
  const activeRooftopSlug =
    rooftopSlug !== ALL_ROOFTOPS && rooftopOptions.some((r) => r.slug === rooftopSlug) ? rooftopSlug : null

  const load = useCallback(() => {
    if (dealerGroupId === null) return undefined
    let cancelled = false
    setPage(undefined)
    setError(null)
    getNotOnWebsite(dealerGroupId, activeRooftopSlug)
      .then((next) => {
        if (!cancelled) setPage(next)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load the page")
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, activeRooftopSlug])

  useEffect(() => load(), [load])

  const handleDownload = async () => {
    if (dealerGroupId === null) return
    setDownloading(true)
    try {
      const blob = await exportNotOnWebsiteWorkbook(dealerGroupId, activeRooftopSlug)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "INVI Not on the Website.xlsx"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // Beside the button, not thrown: a failed download must not take the page down.
      setError(err instanceof Error ? err.message : "Download failed")
    } finally {
      setDownloading(false)
    }
  }

  if (dealerGroupId === null) {
    return (
      <div style={pageStyle(isMobile)}>
        <PageHeading domain={websiteDomain ?? null} />
        <DealerGroupRequiredNotice surface="the Not on the Website page" />
      </div>
    )
  }

  return (
    <div style={pageStyle(isMobile)}>
      <div
        style={{
          display: "flex",
          // A single space-between row runs the controls off a phone's edge.
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "flex-start",
          flexWrap: "wrap",
          gap: THEME.spacing.md,
          marginBottom: THEME.spacing.lg,
        }}
      >
        <PageHeading domain={siteDomain} />
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: THEME.spacing.sm,
            flexWrap: "wrap",
          }}
        >
          {page && (
            <div style={asOfStyle}>
              From the audit run{" "}
              <b style={{ color: INK }}>
                {page.run_completed_at ? formatRunTime(page.run_completed_at) : "—"}
              </b>
            </div>
          )}
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || !page}
            style={downloadButtonStyle(downloading || !page, isMobile)}
          >
            {downloading ? "Preparing…" : "Download Excel"}
          </button>
        </div>
      </div>

      {rooftopOptions.length > 0 && (
        <AuditStoreTabs options={rooftopOptions} value={rooftopSlug} onChange={setRooftopSlug} />
      )}

      {error !== null && (
        <div style={noticeStyle(THEME.colors.semantic.danger)} role="alert">{error}</div>
      )}

      {page === undefined && error === null && (
        <div style={{ fontSize: 12.5, color: MUTED }}>Loading the page…</div>
      )}

      {page === null && (
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
          No audit run yet for this dealer group. The page fills in from the first audit run.
        </div>
      )}

      {page && (
        <ErrorBoundary surface="not-on-website">
          <PageBody page={page} isMobile={isMobile} />
        </ErrorBoundary>
      )}
    </div>
  )
}

function PageHeading({ domain }: { domain: string | null | undefined }) {
  const site = siteName(domain)
  return (
    <div>
      <Link to="/inventory/audit" style={{ fontSize: 12.5, color: MUTED, textDecoration: "none" }}>
        ← Inventory Audit
      </Link>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: "-0.5px",
          margin: `${THEME.spacing.xs} 0 0 0`,
          color: INK,
        }}
      >
        {/* Unknown (undefined) holds the line blank rather than saying "the
            Website" for a moment — Ian saw exactly that flash, 2026-10-09. */}
        {domain === undefined ? "\u00A0" : `Not on ${domain ?? "the Website"}`}
      </h1>
      <div style={{ fontSize: 13, color: MUTED, marginTop: 4, maxWidth: "72ch" }}>
        Cars that are for sale and should be on {site} but are not, and what they are
        worth. Every number is a fact from the latest audit run; nothing is estimated.
      </div>
    </div>
  )
}

/** Which tile's breakdown drawer is open beneath the band, if any. */
type Breakdown = "dark" | "sold"

function PageBody({ page, isMobile }: { page: NotOnWebsiteResponse; isMobile: boolean }) {
  // Ian, 2026-10-08: "when the tile is clicked it could open a drawer drop
  // down and show the breakdown of whats getting counted". One drawer at a time.
  const [openBreakdown, setOpenBreakdown] = useState<Breakdown | null>(null)
  const toggleBreakdown = (which: Breakdown) =>
    setOpenBreakdown((current) => (current === which ? null : which))
  const site = siteName(page.website_domain)
  // The store view names the store beside the site: "Not on dolanautogroup.com · Dodge".
  const storeSuffix = page.rooftop_label ? ` · ${page.rooftop_label}` : ""
  const storeAt = page.rooftop_label ? ` at ${page.rooftop_label}` : ""
  const drawer = openBreakdown !== null
    ? <BreakdownPanel which={openBreakdown} page={page} site={site} onClose={() => setOpenBreakdown(null)} />
    : null

  const darkPoints: StackedPoint[] = page.history.map((point) => ({
    date: parseUtcTimestamp(point.taken_at),
    dollars: point.selling_price_total,
    count: point.cars,
  }))
  const soldPoints: StackedPoint[] = page.history.map((point) => ({
    date: parseUtcTimestamp(point.taken_at),
    dollars: point.sold_selling_price_total,
    count: point.sold_still_advertised,
  }))
  const alertsStartedAt = page.alerts_started_at ? parseUtcTimestamp(page.alerts_started_at) : null

  return (
    <>
      <div style={tilesStyle(isMobile)}>
        {/* The headline tile is two columns wide on a desktop; on a phone the
            grid is one column, where a span would push it past the edge. */}
        {/* Critical red, not brand navy: navy read as "not a big deal" next to
            the sold tile (Ian, 2026-10-09), and both are critical problems. */}
        <TileButton
          accent={CRITICAL}
          label={`Not on ${site}${storeSuffix}`}
          open={openBreakdown === "dark"}
          onClick={() => toggleBreakdown("dark")}
          wide={!isMobile}
        >
          <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <div style={tileNumberStyle(CRITICAL)}>{page.cars.toLocaleString()}</div>
              <div style={tileSubStyle}>
                cars for sale
                <br />
                that shoppers can't find
              </div>
            </div>
            <div>
              <div style={tileNumberStyle(CRITICAL)}>
                {formatSellingPrice(page.selling_price_total, true)}
              </div>
              <div style={tileSubStyle}>
                calculated from HomeNet Selling Price
                {page.no_price_cars > 0 && (
                  <>
                    <br />
                    {page.no_price_cars} with no price, not counted
                  </>
                )}
              </div>
            </div>
          </div>
        </TileButton>
        {/* On a phone the grid is one column, so the drawer sits right under
            the tile that opened it instead of below the whole band. */}
        {isMobile && openBreakdown === "dark" && drawer}

        <div style={tileStyle(CRITICAL)}>
          <div style={tileLabelStyle}>Days dark avg.</div>
          <div style={tileNumberStyle(CRITICAL)}>
            {page.days_dark_avg === null ? "—" : Math.round(page.days_dark_avg).toLocaleString()}
          </div>
          <div style={tileSubStyle}>
            {page.days_dark_oldest === null || page.days_dark_youngest === null ? (
              "no cars dark"
            ) : (
              <>
                oldest <b style={{ color: CRITICAL }}>{page.days_dark_oldest}</b> days
                <br />
                youngest <b style={{ color: CRITICAL }}>{page.days_dark_youngest}</b> days
              </>
            )}
          </div>
        </div>

        {/* The other direction, in the headline tile's shape: the listings and
            what the site is advertising them at. Those cars have left HomeNet,
            so the dollars are the website's own prices, and the caption says so. */}
        <TileButton
          accent={SOLD}
          label={`Sold, still on ${site}${storeSuffix}`}
          open={openBreakdown === "sold"}
          onClick={() => toggleBreakdown("sold")}
          wide={!isMobile}
        >
          <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div>
              <div style={tileNumberStyle(SOLD)}>{page.sold_still_advertised.toLocaleString()}</div>
              <div style={tileSubStyle}>
                listings for cars
                <br />
                you no longer have
              </div>
            </div>
            <div>
              <div style={tileNumberStyle(SOLD)}>
                {formatSellingPrice(page.sold_selling_price_total, true)}
              </div>
              <div style={tileSubStyle}>
                advertised on {site}
                {page.sold_no_price_cars > 0 && (
                  <>
                    <br />
                    {page.sold_no_price_cars} with no price, not counted
                  </>
                )}
              </div>
            </div>
          </div>
        </TileButton>
        {isMobile && openBreakdown === "sold" && drawer}

        <SinceAlertsTile page={page} alertsStartedAt={alertsStartedAt} />
      </div>

      {!isMobile && drawer}

      <div style={chartsStyle(isMobile)}>
        <div style={panelStyle}>
          <div style={panelHeadStyle}>
            <h2 style={panelTitleStyle}>Cars not on {site}{storeSuffix}, and what they are worth</h2>
            <span style={quietStyle}>per audit run · last 60 days · hover a point</span>
          </div>
          <RunStackedChart
            points={darkPoints}
            color={CRITICAL}
            dollarsLabel="HomeNet Selling Price of those cars"
            countLabel={`Cars not on ${site}`}
            countUnit="cars"
            formatDollars={(value) => formatSellingPrice(value, true)}
            ariaLabel="Selling price of cars not on the website per audit run, last 60 days"
            alertsStartedAt={alertsStartedAt}
            emptyText="No runs saved yet. The first point lands with the next audit run."
            viewWidth={isMobile ? NARROW_CHART_WIDTH : undefined}
          />
          <div style={noteStyle}>
            The number falls when a dark car goes live <i>and</i> when a dark car sells or transfers.
            Both are real drops in exposure; only the first is a fix.
          </div>
        </div>
        <div style={panelStyle}>
          <div style={panelHeadStyle}>
            <h2 style={panelTitleStyle}>Sold, still on {site}{storeSuffix}</h2>
            <span style={quietStyle}>per audit run</span>
          </div>
          <RunStackedChart
            points={soldPoints}
            color={SOLD}
            dollarsLabel={`What ${site} is advertising them at`}
            countLabel="Listings for cars you no longer have"
            countUnit="listings"
            formatDollars={(value) => formatSellingPrice(value, true)}
            ariaLabel="Sold cars still advertised, and the website's advertised price, per audit run"
            alertsStartedAt={alertsStartedAt}
            emptyText="No runs saved yet."
            viewWidth={NARROW_CHART_WIDTH}
          />
        </div>
      </div>

      <VehicleListPanel
        title={`The dark cars${storeAt}, longest first`}
        count={page.vehicles.length}
        unit="car"
        tail="the oldest ones are the ones to fix first"
        emptyText={`Every car that should be on ${site} is on it.`}
        vehicles={page.vehicles}
        daysHeading="Days dark"
        priceHeading="Selling price"
        withLink={false}
        pill={pillStyle(CRITICAL, THEME.problemDisplay.severitySoft.critical)}
        isMobile={isMobile}
      />
      <VehicleListPanel
        title={`Sold, still on ${site}${storeSuffix} — the listings, longest up first`}
        count={page.sold_vehicles.length}
        unit="listing"
        tail="each one is a lead on a car you no longer have"
        emptyText="No listing is up for a car you no longer have."
        vehicles={page.sold_vehicles}
        daysHeading="Days listed"
        priceHeading="Advertised price"
        withLink
        pill={pillStyle(SOLD, SOLD_SOFT)}
        isMobile={isMobile}
      />
    </>
  )
}

/**
 * A tile that opens a breakdown drawer beneath the band. Same contract as the
 * audit page's severity tiles: a toggle button carrying aria-expanded, a
 * Show/Hide affordance beside the caption, tinted and fully outlined in its
 * colour while open so it reads as the drawer's source.
 */
function TileButton({
  accent, label, open, onClick, wide, children,
}: {
  accent: string
  label: string
  open: boolean
  onClick: () => void
  wide: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onClick}
      style={{
        ...tileStyle(accent),
        ...(open && { borderColor: accent, background: THEME.problemDisplay.legendBackground }),
        gridColumn: wide ? "span 2" : "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        fontFamily: THEME.fonts.primary,
        cursor: "pointer",
      }}
    >
      <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={tileLabelStyle}>{label}</span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: accent, whiteSpace: "nowrap" }}>
          {open ? "Hide" : "What's counted"}{" "}
          <span aria-hidden="true" style={{ display: "inline-block", transform: open ? "rotate(180deg)" : undefined }}>
            ▾
          </span>
        </span>
      </span>
      {children}
    </button>
  )
}

/**
 * The drawer: every problem the tile adds up, with its cars and dollars, then
 * the same problems still inside their grace window, which the tile does not
 * count yet. The second list is what answers "the audit says 229 and the tile
 * says 1".
 */
function BreakdownPanel({
  which, page, site, onClose,
}: {
  which: Breakdown
  page: NotOnWebsiteResponse
  site: string
  onClose: () => void
}) {
  const isMobile = useIsMobile()
  const accent = which === "dark" ? CRITICAL : SOLD
  const counted = which === "dark"
    ? page.by_problem
    : [{
        problem_type: "gone-still-advertised-escalated",
        label: "Gone, still advertised — over 2 days",
        cars: page.sold_still_advertised,
        no_price_cars: page.sold_no_price_cars,
        selling_price_total: page.sold_selling_price_total,
      }]
  const waiting = which === "dark"
    ? page.inside_window
    : page.sold_inside_window > 0
      ? [{ problem_type: "gone-still-advertised", label: "Gone, still advertised", cars: page.sold_inside_window }]
      : []
  const total = counted.reduce((sum, entry) => sum + entry.cars, 0)
  const unit = which === "dark" ? "cars" : "listings"
  const dollarsNote = which === "dark" ? "HomeNet Selling Price" : `advertised on ${site}`

  return (
    <div
      role="region"
      aria-label={`What the ${which === "dark" ? "Not on the website" : "Sold, still advertised"} tile counts`}
      style={{
        ...panelStyle,
        borderColor: accent,
        marginTop: -6,
      }}
    >
      <div style={panelHeadStyle}>
        <h2 style={panelTitleStyle}>
          What the {total.toLocaleString()} {unit} are
        </h2>
        <button type="button" onClick={onClose} style={linkButtonStyle(accent)}>
          Hide ▴
        </button>
      </div>
      {isMobile ? (
        // A phone has no room for three columns: one block per problem.
        <div style={{ padding: "4px 18px 10px" }}>
          {counted.map((entry) => (
            <div key={entry.problem_type} style={{ padding: "10px 0", borderTop: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 13, color: INK }}>{entry.label}</div>
              <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
                <b style={{ color: INK }}>{entry.cars.toLocaleString()} {unit}</b>
                {" · "}
                {formatSellingPrice(entry.selling_price_total)}
                {entry.no_price_cars > 0 && ` · ${entry.no_price_cars} with no price`}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: THEME.fonts.primary }}>
          <thead>
            <tr>
              <Th>Counted</Th>
              <Th align="right">{unit === "cars" ? "Cars" : "Listings"}</Th>
              <Th align="right">Dollars ({dollarsNote})</Th>
            </tr>
          </thead>
          <tbody>
            {counted.map((entry) => (
              <tr key={entry.problem_type}>
                <Td wrap>{entry.label}</Td>
                <Td align="right">{entry.cars.toLocaleString()}</Td>
                <Td align="right" wrap>
                  {formatSellingPrice(entry.selling_price_total)}
                  {entry.no_price_cars > 0 && (
                    <span style={{ color: MUTED }}> · {entry.no_price_cars} with no price</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ ...noteStyle, paddingBottom: 6 }}>
        <b style={{ color: INK }}>Inside their window, not counted yet.</b> These turn into the rows above once their
        grace runs out — a feed is usually still catching up.
      </div>
      {waiting.length === 0 ? (
        <div style={{ padding: "0 18px 14px", fontSize: 12.5, color: MUTED }}>None right now.</div>
      ) : isMobile ? (
        <div style={{ padding: "0 18px 10px" }}>
          {waiting.map((entry) => (
            <div key={entry.problem_type} style={{ padding: "10px 0", borderTop: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 13, color: INK }}>{entry.label}</div>
              <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
                <b style={{ color: INK }}>{entry.cars.toLocaleString()} {unit}</b> · not counted yet
              </div>
            </div>
          ))}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: THEME.fonts.primary, marginBottom: 4 }}>
          <tbody>
            {waiting.map((entry) => (
              <tr key={entry.problem_type}>
                <Td wrap>{entry.label}</Td>
                <Td align="right">{entry.cars.toLocaleString()}</Td>
                <Td align="right"><span style={{ color: MUTED }}>not counted yet</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function linkButtonStyle(color: string): React.CSSProperties {
  return {
    background: "none",
    border: "none",
    padding: 0,
    color,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

function SinceAlertsTile({ page, alertsStartedAt }: { page: NotOnWebsiteResponse; alertsStartedAt: Date | null }) {
  const change = page.change_since_alerts
  // A fall is the goal; a rise is the fact. Zero is neither.
  const color = change === null || change === 0 ? MUTED : change < 0 ? THEME.problemDisplay.severityColors.expected : CRITICAL
  return (
    <div style={tileStyle(BORDER)}>
      <div style={tileLabelStyle}>Since alerts started</div>
      <div style={tileNumberStyle(color)}>
        {change === null ? "—" : `${change > 0 ? "+" : change < 0 ? "−" : ""}${formatSellingPrice(Math.abs(change), true)}`}
      </div>
      <div style={tileSubStyle}>
        {alertsStartedAt === null ? (
          <>
            first morning email
            <br />
            has not gone out yet
          </>
        ) : change === null ? (
          <>
            first run after the alerts
            <br />
            has not been saved yet
          </>
        ) : (
          <>
            first morning email
            <br />
            {alertsStartedAt.toLocaleDateString([], { month: "short", day: "numeric" })}
          </>
        )}
      </div>
    </div>
  )
}

interface VehicleListProps {
  title: string
  count: number
  unit: "car" | "listing"
  tail: string
  emptyText: string
  vehicles: NotOnWebsiteVehicle[]
  daysHeading: string
  priceHeading: string
  /** The sold listings carry a link to the live page; a dark car has none. */
  withLink: boolean
  /** The problem pill's colours: red for the dark cars, indigo for sold listings. */
  pill: React.CSSProperties
  isMobile: boolean
}

/**
 * A list of cars, longest first: a table on a desktop, a card list on a phone.
 * Used twice — the dark cars, and the sold cars still advertised with their
 * links (Ian, 2026-10-08: "a table of cars for the sold still on site where it
 * shows the same thing + the link").
 */
function VehicleListPanel({
  title, count, unit, tail, emptyText, vehicles, daysHeading, priceHeading, withLink, pill, isMobile,
}: VehicleListProps) {
  return (
    <div style={panelStyle}>
      <div style={panelHeadStyle}>
        <h2 style={panelTitleStyle}>{title}</h2>
        <span style={quietStyle}>
          {count.toLocaleString()} {count === 1 ? unit : `${unit}s`}
          {count > 0 && ` · ${tail}`}
        </span>
      </div>
      {count === 0 ? (
        <div style={{ padding: "18px", fontSize: 13, color: MUTED }}>{emptyText}</div>
      ) : isMobile ? (
        <VehicleCardList vehicles={vehicles} daysHeading={daysHeading} withLink={withLink} pill={pill} />
      ) : (
        <VehicleTable vehicles={vehicles} daysHeading={daysHeading} priceHeading={priceHeading} withLink={withLink} pill={pill} />
      )}
    </div>
  )
}

function VehicleTable({
  vehicles, daysHeading, priceHeading, withLink, pill,
}: {
  vehicles: NotOnWebsiteVehicle[]
  daysHeading: string
  priceHeading: string
  withLink: boolean
  pill: React.CSSProperties
}) {
  return (
    <div style={{ maxHeight: 640, overflowY: "auto", overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: THEME.fonts.primary }}>
        <thead>
          <tr>
            <Th>Stock</Th>
            <Th>Car</Th>
            <Th>Store</Th>
            <Th>Problem</Th>
            <Th align="right">{daysHeading}</Th>
            <Th align="right">Photos</Th>
            <Th align="right">{priceHeading}</Th>
            {withLink && <Th>Listing</Th>}
          </tr>
        </thead>
        <tbody>
          {vehicles.map((vehicle, index) => (
            <tr key={`${vehicle.vin}-${vehicle.problem_type}`} style={{ background: index % 2 === 1 ? THEME.problemDisplay.legendBackground : undefined }}>
              <Td>{vehicle.stock || "—"}</Td>
              <Td>{vehicle.vehicle || "—"}</Td>
              <Td>{vehicle.rooftop || "—"}</Td>
              <Td>
                <span style={pill}>{vehicle.label}</span>
              </Td>
              <Td align="right">{vehicle.days_dark === null ? "—" : vehicle.days_dark}</Td>
              <Td align="right"><PhotosCell count={vehicle.photos_count} /></Td>
              <Td align="right"><PriceCell price={vehicle.selling_price} /></Td>
              {withLink && <Td><ListingLink url={vehicle.website_url} /></Td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The phone view of the same rows: identity, the problem pill, and the three
 * numbers that matter, one card per car. Mirrors the Cleared List's card list
 * (MatchResultsTable.tsx), itself the Image Index pattern.
 */
function VehicleCardList({
  vehicles, daysHeading, withLink, pill,
}: {
  vehicles: NotOnWebsiteVehicle[]
  daysHeading: string
  withLink: boolean
  pill: React.CSSProperties
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.sm, padding: THEME.spacing.md }}>
      {vehicles.map((vehicle) => (
        <div key={`${vehicle.vin}-${vehicle.problem_type}`} style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>{vehicle.vehicle || "—"}</div>
          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
            {[vehicle.stock ? `Stock ${vehicle.stock}` : null, vehicle.rooftop].filter(Boolean).join("  ·  ") || "—"}
          </div>
          <div style={{ marginTop: 8 }}>
            <span style={pill}>{vehicle.label}</span>
          </div>
          <div style={{ display: "flex", gap: THEME.spacing.md, marginTop: 10, flexWrap: "wrap" }}>
            <CardMetric label={daysHeading} value={vehicle.days_dark === null ? "—" : String(vehicle.days_dark)} />
            <CardMetric label="Photos"><PhotosCell count={vehicle.photos_count} /></CardMetric>
            <CardMetric label="Price"><PriceCell price={vehicle.selling_price} /></CardMetric>
          </div>
          {withLink && (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              <ListingLink url={vehicle.website_url} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** The live listing, opened in a new tab; a dash when no URL was recorded. */
function ListingLink({ url }: { url: string | null }) {
  if (!url) return <span style={{ color: MUTED }}>—</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: THEME.problemDisplay.severityColors.info, fontWeight: 600, textDecoration: "none" }}
    >
      View listing →
    </a>
  )
}

function CardMetric({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 0", minWidth: 0 }}>
      <span style={{ fontSize: 10.5, letterSpacing: "0.5px", textTransform: "uppercase", color: MUTED, fontWeight: 600 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: INK, fontVariantNumeric: "tabular-nums" }}>{children ?? value}</span>
    </div>
  )
}

/** Zero photos is the thing to see: it is why a sale-ready car is dark. */
function PhotosCell({ count }: { count: number | null }) {
  if (count === null) return <>—</>
  if (count === 0) return <span style={{ color: CRITICAL, fontWeight: 600 }}>0</span>
  return <>{count}</>
}

function PriceCell({ price }: { price: number | null }) {
  if (price === null) return <span style={{ color: MUTED }}>no price</span>
  return <>{formatSellingPrice(price)}</>
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: "9px 12px",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: THEME.colors.white,
        background: THEME.colors.navy,
        position: "sticky",
        top: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  )
}

function Td({
  children, align = "left", wrap = false,
}: {
  children: React.ReactNode
  align?: "left" | "right"
  /** Lets the cell run to several lines — the drawer's labels on a phone. */
  wrap?: boolean
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "10px 12px",
        fontSize: 13,
        borderTop: `1px solid ${BORDER}`,
        color: INK,
        whiteSpace: wrap ? "normal" : "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {children}
    </td>
  )
}

function formatRunTime(timestamp: string): string {
  return parseUtcTimestamp(timestamp).toLocaleString([], {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  })
}

/** Full width like the audit page; a tighter gutter on a phone. */
function pageStyle(isMobile: boolean): React.CSSProperties {
  return { padding: isMobile ? THEME.spacing.md : THEME.spacing.lg }
}

function tilesStyle(isMobile: boolean): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12,
    marginBottom: 18,
  }
}

function tileStyle(accent: string): React.CSSProperties {
  return {
    borderStyle: "solid",
    borderWidth: "3px 1px 1px 1px",
    borderColor: `${accent} ${BORDER} ${BORDER} ${BORDER}`,
    borderRadius: THEME.borderRadius.md,
    padding: "14px 16px",
    background: THEME.colors.neutral.light.surface,
  }
}

const tileLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: MUTED,
}

function tileNumberStyle(color: string): React.CSSProperties {
  return {
    fontSize: 30,
    fontWeight: 600,
    letterSpacing: "-1px",
    lineHeight: 1.15,
    marginTop: 6,
    color,
    fontVariantNumeric: "tabular-nums",
  }
}

const tileSubStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: MUTED,
  marginTop: 4,
  lineHeight: 1.5,
}

function chartsStyle(isMobile: boolean): React.CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 2fr) minmax(0, 1fr)",
    gap: 18,
  }
}

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${BORDER}`,
  borderRadius: THEME.borderRadius.lg,
  marginBottom: 18,
  overflow: "hidden",
}

const panelHeadStyle: React.CSSProperties = {
  padding: "12px 18px",
  borderBottom: `1px solid ${BORDER}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 12,
  flexWrap: "wrap",
}

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: INK,
}

const quietStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: MUTED,
}

const noteStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: MUTED,
  padding: "10px 18px 14px",
  borderTop: `1px dashed ${BORDER}`,
}

/** The problem pill in a list row: the problem's colour on its soft tint. */
function pillStyle(accent: string, soft: string): React.CSSProperties {
  return {
    display: "inline-block",
    borderRadius: THEME.borderRadius.pill,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.2px",
    padding: "2px 9px",
    background: soft,
    color: accent,
  }
}

const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  fontFamily: THEME.fonts.primary,
}

const asOfStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: MUTED,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${BORDER}`,
  borderRadius: THEME.borderRadius.md,
  padding: "8px 12px",
}

function noticeStyle(color: string): React.CSSProperties {
  return {
    padding: THEME.spacing.md,
    marginBottom: THEME.spacing.md,
    border: `1px solid ${color}`,
    borderRadius: THEME.borderRadius.md,
    background: THEME.colors.white,
    fontSize: 12.5,
    color,
  }
}

function downloadButtonStyle(disabled: boolean, fullWidth: boolean): React.CSSProperties {
  return {
    padding: "7px 14px",
    fontSize: 12.5,
    fontFamily: THEME.fonts.primary,
    borderRadius: THEME.borderRadius.sm,
    border: `1px solid ${THEME.colors.navy}`,
    background: disabled ? THEME.colors.silver : THEME.colors.navy,
    color: THEME.colors.white,
    cursor: disabled ? "not-allowed" : "pointer",
    ...(fullWidth && { width: "100%" }),
  }
}
